import React from 'react';
import { FormGroup, HStack, Box, Checkbox, IconButton, FormRow } from '@cad-arc';
import { FormInput } from '../../../../forms/integrated-form-controls/form-input';
import { FormLocationSelect } from '../../../../forms/integrated-form-controls/form-location-select';
import { FormTextarea } from '../../../../forms/integrated-form-controls/form-text-area';
import { useEnabledFeatures } from '../../../../common/hooks/enabled-features/use-enabled-features';
import { FormInputButton } from '../../../../forms/integrated-form-components/form-button';
import { formatPhoneNumber } from '../../../../event-panel/event-panel-form/helpers/person-helpers';
import { FormSelect } from '../../../../forms/integrated-form-controls/form-select';
import { FormCheckbox } from '../../../../forms/integrated-form-controls/form-checkbox';
import { FormDateInput } from '../../../../forms/integrated-form-controls/form-date-input';
import { useApplicationType } from '../../../../common/hooks/use-application-type';
import { useEntityPanelPersonFieldsetState } from './use-entity-panel-person-fieldset-state';
import { EntityPanelPersonsForm, PersonFieldsetProps } from '../types';
import { EntityPanelFormPersonCardControls } from './entity-panel-form-person-card-controls';
import { useIntegratedForm } from '../../../../forms/use-integrated-form';
import { EventPanelFormOriAliasSelect } from '../../../../event-panel/event-panel-form/event-panel-form-ori-alias-select';
import { useConvertPersonHeightToFeetInchesNumericFormat } from '../../../hooks/use-convert-person-height-to-feet-inches-numeric-format';
import { useFormHasHadDataSetAtLeastOnceContext } from '../../../../event-panel/event-state/form-has-had-data-set-at-least-once';
import { getUniversalSearchLabel } from '../../../../common/helpers/get-usx-labels';

export function EntityPanelFormPersonFieldset(props: PersonFieldsetProps): React.ReactElement {
    const { form } = useIntegratedForm<EntityPanelPersonsForm>();

    const {
        baseKey,
        index,
        remove,
        displayAsCards,
        isReadOnly,
        entityPanelOptions,
        hideQueryInDex,
        entityNumber,
        eventId,
        userManuallyAddedField,
    } = props;
    const isReportingParty = baseKey === 'reportingParties';
    const enabledFeatures = useEnabledFeatures();
    const { isFirstResponder, isCad } = useApplicationType();
    const convertPersonHeightToFeetInchesNumericFormat = useConvertPersonHeightToFeetInchesNumericFormat(
        { baseKey, index }
    );
    const formHasHadDataSetAtLeastOnce = useFormHasHadDataSetAtLeastOnceContext();

    const canDeletePerson =
        baseKey === 'involvedPersons'
            ? props.allowMembersControl
            : props.allowMembersControl && index !== 0;

    const {
        onToggleHeightRange,
        onToggleAgeRange,
        onToggleWeightRange,
        getEventNotificationName,
        setShowExtraFields,
        queryPersonOnSave,
        isPersisted,
        showWeightRange,
        showHeightRange,
        showAgeRange,
        showExtraFields,
        contextId,
        showDexFields,
        showInvolvementTypeOther,
    } = useEntityPanelPersonFieldsetState(props);

    const shouldAutoFocusFirstNameInput = formHasHadDataSetAtLeastOnce && userManuallyAddedField;

    const toggleOnBlurDisabled = {
        inputProps: {
            toggleOnBlur: false,
        },
    };

    return (
        <>
            <FormGroup spacing={1} data-testid={`${baseKey}-${index}`}>
                <EntityPanelFormPersonCardControls
                    remove={remove}
                    allowMembersControl={canDeletePerson}
                    form={form}
                    baseKey={baseKey}
                    index={index}
                    isNewEntity={!displayAsCards}
                    isReadOnly={isReadOnly}
                    entityNumber={entityNumber}
                    eventId={eventId}
                />
                <HStack
                    alignItems="center"
                    data-testid={`${
                        isReportingParty ? 'reporting-party' : 'involved-person'
                    }-card-${contextId ?? 'new'}`}
                >
                    <FormRow>
                        <FormInput
                            length={isFirstResponder ? 'md' : 'sm'}
                            label="First Name"
                            control={form.control}
                            name={`${baseKey}.${index}.firstName`}
                            asEditable={!!isPersisted}
                            eventNotificationName={getEventNotificationName('FIRST_NAME')}
                            mode={isReadOnly ? 'read-only' : undefined}
                            autoFocus={shouldAutoFocusFirstNameInput}
                        />
                        <FormInput
                            length={isFirstResponder ? 'md' : 'sm'}
                            control={form.control}
                            label="Last Name"
                            name={`${baseKey}.${index}.lastName`}
                            showRequiredIndicator={
                                isCad &&
                                enabledFeatures.reportingPartyNameRequired &&
                                isReportingParty
                            }
                            asEditable={!!isPersisted}
                            eventNotificationName={getEventNotificationName('LAST_NAME')}
                            mode={isReadOnly ? 'read-only' : undefined}
                        />
                        <FormInput
                            length={isFirstResponder ? 'md' : 'sm'}
                            control={form.control}
                            label="M.I."
                            name={`${baseKey}.${index}.middleName`}
                            asEditable={!!isPersisted}
                            mode={isReadOnly ? 'read-only' : undefined}
                        />
                    </FormRow>
                </HStack>
                <FormRow>
                    <FormInput
                        length="md"
                        control={form.control}
                        label="Phone"
                        name={`${baseKey}.${index}.phoneNumber`}
                        eventNotificationName={getEventNotificationName('PHONE_NUMBER')}
                        renderViewMode={formatPhoneNumber}
                        asEditable={!!isPersisted}
                        mode={isReadOnly ? 'read-only' : undefined}
                    />
                    <FormInput
                        length="sm"
                        control={form.control}
                        label="Ext."
                        name={`${baseKey}.${index}.phoneExtension`}
                        eventNotificationName={getEventNotificationName('PHONE_EXTENSION')}
                        asEditable={!!isPersisted}
                        mode={isReadOnly ? 'read-only' : undefined}
                    />
                </FormRow>
                <FormRow>
                    {showAgeRange ? (
                        <>
                            <FormInput
                                control={form.control}
                                label="Min Age"
                                name={`${baseKey}.${index}.ageMin`}
                                length="sm"
                                asEditable={!!isPersisted}
                                eventNotificationName={getEventNotificationName('AGE_MIN')}
                                mode={isReadOnly ? 'read-only' : undefined}
                            />
                            <FormInput
                                control={form.control}
                                label="Max Age"
                                name={`${baseKey}.${index}.ageMax`}
                                length="sm"
                                asEditable={!!isPersisted}
                                eventNotificationName={getEventNotificationName('AGE_MAX')}
                                mode={isReadOnly ? 'read-only' : undefined}
                            />
                        </>
                    ) : (
                        <FormDateInput
                            control={form.control}
                            label="Date of Birth"
                            name={`${baseKey}.${index}.dateOfBirth`}
                            length="md"
                            asEditable={!!isPersisted}
                            eventNotificationName={getEventNotificationName('DATE_OF_BIRTH')}
                            mode={isReadOnly ? 'read-only' : undefined}
                        />
                    )}
                    <Box alignSelf="end">
                        <Checkbox
                            data-testid="show-age-range"
                            isChecked={showAgeRange}
                            onChange={() => onToggleAgeRange()}
                            isDisabled={isReadOnly}
                        >
                            Unknown
                        </Checkbox>
                    </Box>
                </FormRow>
                <FormRow>
                    <FormLocationSelect
                        control={form.control}
                        label="Location"
                        name={`${baseKey}.${index}.location`}
                        asEditable={!!isPersisted}
                        placeInputOutside
                        length={enabledFeatures.subpremiseSupport ? undefined : 'lg'}
                        eventNotificationName={getEventNotificationName('LOCATION')}
                        mode={isReadOnly ? 'read-only' : undefined}
                        editableProps={toggleOnBlurDisabled}
                    />
                    {!enabledFeatures.subpremiseSupport && (
                        <FormInput
                            control={form.control}
                            label="Apt, Unit"
                            name={`${baseKey}.${index}.locationSubPremise`}
                            asEditable={!!isPersisted}
                            length="sm"
                            eventNotificationName={getEventNotificationName('LOCATION_SUB_PREMISE')}
                            mode={isReadOnly ? 'read-only' : undefined}
                        />
                    )}
                </FormRow>
                <FormTextarea
                    control={form.control}
                    label="Notes"
                    name={`${baseKey}.${index}.notes`}
                    asEditable={!!isPersisted}
                    eventNotificationName={getEventNotificationName('NOTES')}
                    mode={isReadOnly ? 'read-only' : undefined}
                />
                <FormRow>
                    <FormSelect
                        label="Involvement Type"
                        control={form.control}
                        name={`${baseKey}.${index}.involvementTypeOption`}
                        options={entityPanelOptions.personInvolvementTypeOptions}
                        asEditable={!!isPersisted}
                        length={showInvolvementTypeOther ? 'md' : undefined}
                        mode={isReadOnly ? 'read-only' : undefined}
                    />
                    {showInvolvementTypeOther && (
                        <FormInput
                            label="Description"
                            control={form.control}
                            name={`${baseKey}.${index}.involvementTypeOther`}
                            asEditable={!!isPersisted}
                            length="sm"
                            mode={isReadOnly ? 'read-only' : undefined}
                        />
                    )}
                </FormRow>
                <FormRow>
                    <FormLocationSelect
                        control={form.control}
                        label="Address"
                        name={`${baseKey}.${index}.address`}
                        asEditable={!!isPersisted}
                        placeInputOutside
                        length={enabledFeatures.subpremiseSupport ? undefined : 'lg'}
                        eventNotificationName={getEventNotificationName('ADDRESS')}
                        mode={isReadOnly ? 'read-only' : undefined}
                    />
                    {!enabledFeatures.subpremiseSupport && (
                        <FormInput
                            control={form.control}
                            label="Apt, Unit"
                            name={`${baseKey}.${index}.addressSubPremise`}
                            asEditable={!!isPersisted}
                            length="sm"
                            eventNotificationName={getEventNotificationName('ADDRESS_SUB_PREMISE')}
                            mode={isReadOnly ? 'read-only' : undefined}
                        />
                    )}
                </FormRow>
                <FormRow>
                    {!hideQueryInDex && showDexFields && (
                        <>
                            <FormCheckbox
                                label={`Query in ${getUniversalSearchLabel(
                                    enabledFeatures.dexNextEnabled
                                )}`}
                                name={`${baseKey}.${index}.queryInDex`}
                                control={form.control}
                                asEditable={!!isPersisted}
                                length={isFirstResponder ? undefined : 'sm'}
                                size="sm"
                            />
                            {queryPersonOnSave && (
                                <>
                                    {entityPanelOptions.purposeCodeOptions.length !== 0 && (
                                        <FormSelect
                                            length={isFirstResponder ? 'md' : 'sm'}
                                            control={form.control}
                                            label="Purpose Code"
                                            name={`${baseKey}.${index}.purposeCode`}
                                            asEditable={!!isPersisted}
                                            options={entityPanelOptions.purposeCodeOptions}
                                        />
                                    )}
                                    {entityPanelOptions.imageIndicatorOptions.length !== 0 && (
                                        <FormSelect
                                            length={isFirstResponder ? 'md' : 'sm'}
                                            control={form.control}
                                            asEditable={!!isPersisted}
                                            options={entityPanelOptions.imageIndicatorOptions}
                                            name={`${baseKey}.${index}.imageIndicator`}
                                            label="Image Indicator"
                                        />
                                    )}
                                    <EventPanelFormOriAliasSelect
                                        isFirstResponder={isFirstResponder}
                                        isPersisted={isPersisted}
                                    />
                                </>
                            )}
                        </>
                    )}
                </FormRow>
                {showExtraFields && (
                    <>
                        <FormRow>
                            <FormInput
                                control={form.control}
                                label="DL #"
                                name={`${baseKey}.${index}.driversLicenseNumber`}
                                length="md"
                                asEditable={!!isPersisted}
                                eventNotificationName={getEventNotificationName(
                                    'DRIVERS_LICENSE_NUMBER'
                                )}
                                mode={isReadOnly ? 'read-only' : undefined}
                            />
                            <FormSelect
                                options={entityPanelOptions.stateOptions}
                                control={form.control}
                                label="DL State"
                                name={`${baseKey}.${index}.driversLicenseState`}
                                length="sm"
                                asEditable={!!isPersisted}
                                eventNotificationName={getEventNotificationName(
                                    'DRIVERS_LICENSE_STATE'
                                )}
                                mode={isReadOnly ? 'read-only' : undefined}
                            />
                            <FormInput
                                control={form.control}
                                label="SSN"
                                name={`${baseKey}.${index}.socialSecurityNumber`}
                                length="sm"
                                asEditable={!!isPersisted}
                                eventNotificationName={getEventNotificationName(
                                    'SOCIAL_SECURITY_NUMBER'
                                )}
                                mode={isReadOnly ? 'read-only' : undefined}
                            />
                        </FormRow>
                        <FormRow>
                            <FormSelect
                                options={entityPanelOptions.raceOptions}
                                control={form.control}
                                label="Race"
                                name={`${baseKey}.${index}.race`}
                                length={isFirstResponder ? 'md' : 'sm'}
                                asEditable={!!isPersisted}
                                eventNotificationName={getEventNotificationName('RACE')}
                                mode={isReadOnly ? 'read-only' : undefined}
                            />
                            <FormSelect
                                options={entityPanelOptions.sexOptions}
                                control={form.control}
                                label="Sex"
                                name={`${baseKey}.${index}.sex`}
                                length={isFirstResponder ? 'md' : 'sm'}
                                asEditable={!!isPersisted}
                                eventNotificationName={getEventNotificationName('SEX')}
                                mode={isReadOnly ? 'read-only' : undefined}
                            />
                        </FormRow>
                        <FormRow>
                            {showHeightRange ? (
                                <>
                                    <FormInput
                                        label="Min Height"
                                        length="sm"
                                        control={form.control}
                                        name={`${baseKey}.${index}.heightMin`}
                                        eventNotificationName={getEventNotificationName(
                                            'HEIGHT_MIN'
                                        )}
                                        asEditable={!!isPersisted}
                                        mode={isReadOnly ? 'read-only' : undefined}
                                        onBlur={() =>
                                            convertPersonHeightToFeetInchesNumericFormat(
                                                'heightMin'
                                            )
                                        }
                                    />
                                    <FormInput
                                        label="Max Height"
                                        length="sm"
                                        control={form.control}
                                        name={`${baseKey}.${index}.heightMax`}
                                        eventNotificationName={getEventNotificationName(
                                            'HEIGHT_MAX'
                                        )}
                                        asEditable={!!isPersisted}
                                        mode={isReadOnly ? 'read-only' : undefined}
                                        onBlur={() =>
                                            convertPersonHeightToFeetInchesNumericFormat(
                                                'heightMax'
                                            )
                                        }
                                    />
                                </>
                            ) : (
                                <FormInput
                                    label="Height"
                                    control={form.control}
                                    name={`${baseKey}.${index}.height`}
                                    eventNotificationName={getEventNotificationName('HEIGHT')}
                                    asEditable={!!isPersisted}
                                    length="sm"
                                    mode={isReadOnly ? 'read-only' : undefined}
                                    onBlur={() =>
                                        convertPersonHeightToFeetInchesNumericFormat('height')
                                    }
                                />
                            )}
                            <Box alignSelf="end">
                                <Checkbox
                                    data-testid="show-height-range"
                                    isChecked={showHeightRange}
                                    onChange={() => onToggleHeightRange()}
                                    isDisabled={isReadOnly}
                                >
                                    Unknown
                                </Checkbox>
                            </Box>
                        </FormRow>

                        <FormRow>
                            {showWeightRange ? (
                                <>
                                    <FormInput
                                        control={form.control}
                                        label="Min Weight"
                                        name={`${baseKey}.${index}.weightMin`}
                                        length="sm"
                                        asEditable={!!isPersisted}
                                        eventNotificationName={getEventNotificationName(
                                            'WEIGHT_MIN'
                                        )}
                                        mode={isReadOnly ? 'read-only' : undefined}
                                    />
                                    <FormInput
                                        label="Max Weight"
                                        control={form.control}
                                        name={`${baseKey}.${index}.weightMax`}
                                        length="sm"
                                        asEditable={!!isPersisted}
                                        eventNotificationName={getEventNotificationName(
                                            'WEIGHT_MAX'
                                        )}
                                        mode={isReadOnly ? 'read-only' : undefined}
                                    />
                                </>
                            ) : (
                                <FormInput
                                    control={form.control}
                                    label="Weight"
                                    name={`${baseKey}.${index}.weight`}
                                    length="sm"
                                    asEditable={!!isPersisted}
                                    eventNotificationName={getEventNotificationName('WEIGHT')}
                                    mode={isReadOnly ? 'read-only' : undefined}
                                />
                            )}
                            <Box alignSelf="end">
                                <Checkbox
                                    data-testid="show-weight-range"
                                    isChecked={showWeightRange}
                                    onChange={() => onToggleWeightRange()}
                                    isDisabled={isReadOnly}
                                >
                                    Unknown
                                </Checkbox>
                            </Box>
                        </FormRow>

                        <FormRow>
                            <FormSelect
                                options={entityPanelOptions.eyeColorOptions}
                                control={form.control}
                                label="Eye Color"
                                name={`${baseKey}.${index}.eyeColor`}
                                length={isFirstResponder ? 'md' : 'sm'}
                                asEditable={!!isPersisted}
                                eventNotificationName={getEventNotificationName('EYE_COLOR')}
                                mode={isReadOnly ? 'read-only' : undefined}
                            />
                            <FormSelect
                                options={entityPanelOptions.hairColorOptions}
                                control={form.control}
                                label="Hair Color"
                                name={`${baseKey}.${index}.hairColor`}
                                length={isFirstResponder ? 'md' : 'sm'}
                                asEditable={!!isPersisted}
                                eventNotificationName={getEventNotificationName('HAIR_COLOR')}
                                mode={isReadOnly ? 'read-only' : undefined}
                            />
                        </FormRow>
                    </>
                )}
            </FormGroup>
            {displayAsCards ? (
                <Box width="100%" pt={4}>
                    <IconButton
                        w="100%"
                        variant="ghost"
                        onClick={() => setShowExtraFields((last) => !last)}
                        icon={showExtraFields ? 'Close' : 'Open'}
                        data-testid={`${baseKey}-${index}-expand`}
                    />
                </Box>
            ) : (
                <FormInputButton
                    size="sm"
                    variant="ghost"
                    leadingVisual={showExtraFields ? 'Close' : 'Open'}
                    onClick={() => setShowExtraFields((last) => !last)}
                >
                    {showExtraFields ? 'Hide details' : 'Show details'}
                </FormInputButton>
            )}
        </>
    );
}
